import {
  DndContext,
  DragEndEvent,
  DragStartEvent,
  rectIntersection,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
} from "@dnd-kit/core";
import { AxiosError } from "axios";
import isEmpty from "lodash/isEmpty";
import { usePostHog } from "posthog-js/react";
import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Stack } from "@/core/components/ui/stack";
import { toast } from "@/core/components/ui/toast";
import { Typography } from "@/core/components/ui/typography";
import { useBusiness } from "@/core/business";
import {
  REFERRAL_COLUMN,
  REFERRAL_COLUMN_TO_STATUS,
  COLUMN_TO_UPDATE_MODAL_INFO,
  REFERRAL_STATUS_TO_COLUMN,
} from "@/referrals/constants";
import { trackReferralUpdate } from "@/referrals/events";
import { useUpdateReferral } from "@/referrals/query";
import type {
  IReferral,
  ReferralColumn as ReferralColumnType,
  ReferralUpdateParams,
} from "@/referrals/referral";
import { ReferralStats } from "@/referrals/referral-stats";
import type { UpdateInfoModal } from "@/referrals/referrals";
import { groupReferralsByColumnStatuses } from "@/referrals/utils";
import { ReferralCard } from "./referral-card";
import ReferralColumn from "./referral-column";

const COLUMNS = [
  REFERRAL_COLUMN.new,
  REFERRAL_COLUMN.inProgress,
  REFERRAL_COLUMN.scheduled,
  REFERRAL_COLUMN.closed,
] as const;

interface ReferralKanbanViewProps {
  referrals: IReferral[];
  isLoadingReferrals: boolean;
  selectedReferral: IReferral | null;
  setUpdateInfoModal: (modal: UpdateInfoModal | null) => void;
  updateInfoModal: UpdateInfoModal | null;
  onUpdateReferral: (
    values: Partial<ReferralUpdateParams>,
    referral: IReferral,
  ) => void;
  onDeleteReferral: (referral: IReferral) => void;
  onCardClick: (referral: IReferral) => void;
  onPayout: (referral: IReferral | null) => void;
}

const ReferralKanbanView = ({
  referrals,
  isLoadingReferrals,
  selectedReferral,
  setUpdateInfoModal,
  onUpdateReferral,
  onDeleteReferral,
  onCardClick,
  onPayout,
}: ReferralKanbanViewProps) => {
  const posthog = usePostHog();
  const navigate = useNavigate();

  const { businessId } = useBusiness();
  const { mutate: updateReferral } = useUpdateReferral(businessId);

  const [activeColumn, setActiveColumn] = useState<
    ReferralColumnType | undefined
  >();
  const [activeId, setActiveId] = useState<string | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 10,
      },
    }),
  );

  const handleDragStart = useCallback(
    (event: DragStartEvent) => {
      const activeReferral = referrals.find((r) => r.id === event.active.id);
      setActiveId(event.active.id.toString());
      if (activeReferral) {
        setActiveColumn(
          REFERRAL_STATUS_TO_COLUMN[
            activeReferral.status as keyof typeof REFERRAL_STATUS_TO_COLUMN
          ],
        );
      }
    },
    [referrals],
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      setActiveId(null);
      const { active, over } = event;
      setActiveColumn(undefined);

      if (!over) return;

      const activeReferral = referrals.find((r) => r.id === active.id);
      if (!activeReferral) return;

      const overColumn = over.id as ReferralColumnType;
      const newStatus = REFERRAL_COLUMN_TO_STATUS[overColumn];

      if (!newStatus) return;
      if (activeReferral.status === newStatus) return;

      // If dragging the referral to a new column, check if the referral has the required fields.
      // If it does not, show a modal to the user to fill in the missing information.
      const modalInfo = COLUMN_TO_UPDATE_MODAL_INFO[overColumn];
      if (modalInfo) {
        const missingFields = modalInfo.requiredFields.filter((field) =>
          isEmpty(activeReferral[field]),
        );

        if (missingFields.length > 0) {
          setUpdateInfoModal({
            referral: activeReferral,
            targetColumn: overColumn,
          });
          return;
        }
      }

      // Update the referral status using the mutation
      updateReferral(
        {
          id: active.id.toString(),
          updates: {
            status: newStatus,
          },
        },
        {
          onSuccess: (data) => {
            toast.success("Referral status updated successfully");
            trackReferralUpdate(posthog, {
              referral: activeReferral,
              updates: { status: newStatus },
              method: "drag_and_drop",
              success: true,
            });

            if (selectedReferral?.id === data.id) {
              navigate(`/referrals?selected=${data.id}`, { replace: true });
            }
          },
          onError: (error) => {
            trackReferralUpdate(posthog, {
              referral: activeReferral,
              updates: { status: newStatus },
              method: "drag_and_drop",
              success: false,
              error,
            });
            let title = "Failed to update referral status";
            if (error instanceof AxiosError) {
              // Check if the error field is an array or string and use accordingly
              title = Array.isArray(error.response?.data?.error) ? (
                <Stack gap="sm">
                  {error.response?.data?.error.map((e: string) => (
                    <Typography
                      variant="captionW1"
                      textColor="primaryInverse"
                      key={e}
                    >
                      {e}
                    </Typography>
                  ))}
                </Stack>
              ) : (
                error.response?.data?.error || title
              );
            }
            toast.error(title);
          },
        },
      );
    },
    [referrals, updateReferral, toast, selectedReferral, posthog, navigate],
  );

  const groupedReferrals = useMemo(
    () => groupReferralsByColumnStatuses(referrals),
    [referrals],
  );

  const activeReferral = useMemo(
    () => referrals.find((r) => r.id === activeId),
    [referrals, activeId],
  );

  return (
    <>
      <DndContext
        sensors={sensors}
        collisionDetection={rectIntersection}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <Stack
          direction="row"
          className="mx-auto min-w-min max-w-[1680px] p-8 pt-0"
          justify="between"
          fullWidth
        >
          <Stack direction="row">
            {COLUMNS.map((column) => (
              <ReferralColumn
                key={column}
                column={column}
                isLoading={isLoadingReferrals}
                referralsMap={groupedReferrals}
                activeColumn={activeColumn}
                activeId={activeId}
                selectedReferralId={selectedReferral?.id}
                onCardClick={onCardClick}
                onNextColumn={(column, referral) => {
                  setUpdateInfoModal({
                    referral: referral,
                    targetColumn: column,
                  });
                }}
                onPayout={onPayout}
                onLost={(referral) =>
                  onUpdateReferral({ status: "LOST" }, referral)
                }
                onMoveToPaid={(referral) =>
                  onUpdateReferral({ status: "PAID" }, referral)
                }
                onDelete={onDeleteReferral}
              />
            ))}
          </Stack>
          <ReferralStats />
        </Stack>
        <DragOverlay>
          {activeId && activeReferral && (
            <div className="rotate-[-10deg] opacity-80">
              <ReferralCard
                referral={activeReferral}
                column={
                  REFERRAL_STATUS_TO_COLUMN[
                    activeReferral.status as keyof typeof REFERRAL_STATUS_TO_COLUMN
                  ]
                }
                isDragging={false}
                isSelected={false}
                onClick={() => {}}
                onNextColumn={() => {}}
                onPayout={() => {}}
              />
            </div>
          )}
        </DragOverlay>
      </DndContext>
    </>
  );
};

export default ReferralKanbanView;
